import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createZeovitRecommendation,
  getZeovitInputs,
  listZeovitRecommendations,
  runZeovitCheck,
  type CreateZeovitRecommendationInput,
  type ZeovitCheckRequest,
  type ZeovitCheckResult,
  type ZeovitInputs,
  type ZeovitRecommendationListItem,
  type ZeovitRecommendationResult,
} from '../services/zeovitRecommendationService';
import { zeovitRecommendationKeys } from './useZeovitRecommendation';

// The key factory and the single-recommendation query live in ./useZeovitRecommendation
// (shared with the recommendation detail page); re-exported here for the check pages.
export { useZeovitRecommendation, zeovitRecommendationKeys } from './useZeovitRecommendation';

export function useZeovitRecommendations(
  aquariumId: string | undefined,
): UseQueryResult<ZeovitRecommendationListItem[], Error> {
  return useQuery({
    queryKey: zeovitRecommendationKeys.list(aquariumId),
    queryFn: () => listZeovitRecommendations(aquariumId as string),
    enabled: Boolean(aquariumId),
  });
}

/** Cache key of the stored inputs (under the aquarium's list key, refreshed with it). */
export function zeovitInputsQueryKey(aquariumId: string | undefined) {
  return [...zeovitRecommendationKeys.list(aquariumId), 'inputs'] as const;
}

/**
 * The latest stored measurements and ICP analysis a recommendation / check would use
 * (GET …/zeovit-recommendations/inputs, no AI call).
 */
export function useZeovitInputs(aquariumId: string | undefined): UseQueryResult<ZeovitInputs, Error> {
  return useQuery({
    queryKey: zeovitInputsQueryKey(aquariumId),
    queryFn: () => getZeovitInputs(aquariumId as string),
    enabled: Boolean(aquariumId),
  });
}

/** The backend lists newest first, so the latest recommendation is the first row. */
function selectLatest(
  recommendations: ZeovitRecommendationListItem[],
): ZeovitRecommendationListItem | null {
  return recommendations[0] ?? null;
}

/**
 * The aquarium's most recent ZeoVit recommendation (or `null` when there is none yet).
 * Shares the list query (and its cache) with `useZeovitRecommendations`.
 */
export function useLatestZeovitRecommendation(
  aquariumId: string | undefined,
): UseQueryResult<ZeovitRecommendationListItem | null, Error> {
  return useQuery({
    queryKey: zeovitRecommendationKeys.list(aquariumId),
    queryFn: () => listZeovitRecommendations(aquariumId as string),
    select: selectLatest,
    enabled: Boolean(aquariumId),
  });
}

/**
 * Generates a new recommendation. On success the aquarium's recommendation list (and,
 * as its key is a prefix, every cached recommendation of the aquarium) is invalidated,
 * so the card section, the iteration history and the detail pages pick up the new row.
 */
export function useCreateZeovitRecommendation(
  aquariumId: string | undefined,
): UseMutationResult<ZeovitRecommendationResult, Error, CreateZeovitRecommendationInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateZeovitRecommendationInput) => {
      if (!aquariumId) {
        return Promise.reject(new Error('aquariumId is required'));
      }
      return createZeovitRecommendation(aquariumId, input);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: zeovitRecommendationKeys.list(aquariumId) }),
  });
}

export interface RunZeovitCheckVariables extends ZeovitCheckRequest {
  readonly aquariumId: string;
  readonly recommendationId: string;
}

/**
 * Runs a periodic check. On success every cached recommendation of the aquarium is
 * invalidated: the checked one now carries its classification and the list may hold
 * the next iteration.
 */
export function useRunZeovitCheck(): UseMutationResult<ZeovitCheckResult, Error, RunZeovitCheckVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ aquariumId, recommendationId, ...request }: RunZeovitCheckVariables) =>
      runZeovitCheck(aquariumId, recommendationId, request),
    onSuccess: (_result, { aquariumId }) =>
      queryClient.invalidateQueries({ queryKey: zeovitRecommendationKeys.list(aquariumId) }),
  });
}
